/*
 * Description:
 * ---------------------
 * The Firewall screen's device groups. Each group is drawn as what it does:
 * its devices on the left, a line to where they may go on the right, solid
 * when locked to its places, dashed while the box learns them, grey when open,
 * and under a locked group what it stopped. A group that learned lists the
 * places its devices used, who runs each, on what port, by how many devices
 * and when, and locks to them in one click, leaving out a place only this
 * site's group reaches (from the server's comparison of sites) and offering
 * an undo. Groups are made and changed in a sheet: a name, the devices from
 * the ones the box sees or typed by hardware address, and how to start,
 * learning first or with the places known. Devices whose names look alike are
 * offered as a group.
 *
 * A group is a device profile (cloud-anchor/pkg/devicectl), saved whole
 * through /devices/controls/profile, so what Device Speed Limits sets on it
 * goes back as it came; its devices are assigned one by one through
 * /devices/controls/device. Learning is /firewall/learn. The panel reads both
 * and hands them here. Wording in firewallWords.js.
 *
 * ! file components/FirewallGroups.js
 */
import React, { useEffect, useRef, useState } from "react";
import { FiEdit2, FiEyeOff, FiPlus, FiSearch, FiTrash2, FiX } from "react-icons/fi";
import { TbAlertTriangle, TbBan, TbLock, TbLockOpen, TbUsersPlus, TbWorld } from "react-icons/tb";
import { FEATURE_PRESENT } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import { useConfirm } from "./ConfirmModal";
import { kindIcon } from "./FirewallDevices";
import * as DW from "./deviceWords";
import AddressChips from "./AddressChips";
import { AiIcon } from "./AiIcon";
import * as W from "./firewallWords";
import "./RemoteDevices.css";

const STATE_ICONS = { on: TbLock, ai: AiIcon, off: TbLockOpen };

/* What a watched group reached, each place on its own line. */
function Learned({ watched }) {
  if (!watched?.places?.length) return null;
  return (
    <details className="fw-learned">
      <summary>Where they connected ({watched.places.length})</summary>
      <table>
        <tbody>
          {watched.places.map((p) => {
            const r = W.placeWords(p);
            return (
              <tr key={`${p.protocol}/${p.address}/${p.port}`}>
                <td><code>{r.where}</code></td>
                <td>{r.owner}</td>
                <td>{W.plural(r.devices, "device")}</td>
                <td className="is-tried">{r.tried ? W.TRIED : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </details>
  );
}

function DevicePicker({ rows, picked, setPicked, self, nameOf }) {
  const [q, setQ] = useState("");
  const [typed, setTyped] = useState("");
  const needle = q.trim().toLowerCase();
  const ordered = [...rows].sort((a, b) => Number(b.profile === self && !!self) - Number(a.profile === self && !!self));
  const list = needle ? ordered.filter((d) => `${d.name} ${d.ip} ${d.mac}`.toLowerCase().includes(needle)) : ordered;
  const toggle = (mac) => setPicked(picked.includes(mac) ? picked.filter((m) => m !== mac) : [...picked, mac]);
  // Select all takes devices in no group or this one, never another group's.
  const free = list.filter((d) => !d.profile || d.profile === self);
  const allShown = free.length > 0 && free.every((d) => picked.includes(d.mac));
  const pickShown = () => setPicked(allShown
    ? picked.filter((m) => !free.some((d) => d.mac === m))
    : [...new Set([...picked, ...free.map((d) => d.mac)])]);
  // A device switched off is not seen, so it can be added by its hardware address.
  const mac = DW.normalizeMac(typed);
  const typedOk = W.MAC.test(mac);
  const addTyped = (e) => {
    e.preventDefault();
    if (!typedOk) return;
    if (!picked.includes(mac)) setPicked([...picked, mac]);
    setTyped("");
  };
  const extra = picked.filter((m) => !rows.some((d) => d.mac === m)).map((m) => ({ mac: m, name: m, ip: "", profile: "" }));
  return (
    <div className="fw-devices">
      <div className="fw-devices-bar">
        <label className="fw-search">
          <FiSearch size={14} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a device, e.g. till" aria-label="Find a device" />
        </label>
        <button type="button" className="fw-btn is-quiet" onClick={pickShown} disabled={!free.length}>
          {allShown ? W.UNPICK_SHOWN : W.PICK_SHOWN(free.length)}
        </button>
      </div>
      {rows.length === 0 && <p className="fw-muted">{W.NO_DEVICES_SEEN}</p>}
      <ul>
        {[...extra, ...list].map((d) => {
          const on = picked.includes(d.mac);
          const other = d.profile && d.profile !== self ? nameOf(d.profile) : "";
          const Kind = kindIcon(d.name);
          return (
            <li key={d.mac}>
              <label className={`fw-device${on ? " is-on" : ""}`}>
                <input type="checkbox" checked={on} onChange={() => toggle(d.mac)} />
                <span className="fw-device-icon" aria-hidden="true"><Kind size={18} strokeWidth={1.8} /></span>
                <span className="fw-device-name">
                  <strong>{d.name}</strong>
                  <small>{[d.ip !== d.name && d.ip, d.mac !== d.name && d.mac].filter(Boolean).join(" · ") || W.NOT_SEEN}</small>
                </span>
                {other && <span className="fw-in">{W.MOVES_FROM(other)}</span>}
              </label>
            </li>
          );
        })}
      </ul>
      {rows.some((d) => d.profile && d.profile !== self) && <p className="fw-muted">{W.MOVES_NOTE}</p>}
      <div className="fw-add">
        <input value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTyped(e)}
          placeholder={W.MAC_ADD} aria-label="Hardware address to add" />
        <button type="button" className="fw-btn" onClick={addTyped} disabled={!typedOk}><FiPlus size={14} aria-hidden="true" /> Add</button>
      </div>
    </div>
  );
}

/* The kinds of traffic a group's devices may not use, as pills to toggle. */
function KindPills({ classList, blocked, setBlocked, taken }) {
  const offered = classList.filter((c) => !taken.includes(c.key));
  const toggle = (key) => setBlocked(blocked.includes(key) ? blocked.filter((k) => k !== key) : [...blocked, key]);
  const pills = (list) => list.length > 0 && (
    <div className="fw-kinds">
      {list.map((c) => {
        const on = blocked.includes(c.key);
        return (
          <button key={c.key} type="button" title={c.hint} aria-pressed={on}
            className={`fw-pill${on ? " is-blocked" : ""}`} onClick={() => toggle(c.key)}>
            {on && <TbBan size={13} aria-hidden="true" />} {c.label}
          </button>
        );
      })}
    </div>
  );
  return (
    <>
      {pills(offered.filter((c) => !c.isApp))}
      {pills(offered.filter((c) => c.isApp))}
    </>
  );
}

/* A new group, or a change to one, in a sheet from the right: name, devices,
   where they may go, and the kinds of traffic they may not use. */
function GroupSheet({ profile, isNew, start, controls, rows, classList, watched, busy, nameOf, onSave, onClose, onDelete }) {
  const confirm = useConfirm();
  const ref = useRef(null);
  const self = profile.id || "";
  const [name, setName] = useState(profile.name || "");
  const [picked, setPicked] = useState(start?.macs || rows.filter((d) => self && d.profile === self).map((d) => d.mac));
  const [how, setHow] = useState(isNew ? "learn" : (profile.destinations || []).length ? "only" : watched ? "learn" : "anywhere");
  const [places, setPlaces] = useState(profile.destinations || []);
  const [blocked, setBlocked] = useState(Object.keys(DW.blockedClasses(profile.classes)));
  const newDevices = !!self && controls.new_devices === self;
  const lineRows = DW.lineClasses(profile.classes);
  useEffect(() => { ref.current?.focus(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    const classes = { ...lineRows };
    for (const id of blocked) classes[id] = { block: true };
    const destinations = how === "only" ? places : [];
    const newlyHeld = destinations.length > 0 && !(profile.destinations || []).length;
    if (newDevices && newlyHeld && !(await confirm(W.NEW_DEVICES_ASK))) return;
    onSave({ ...profile, name: name.trim(), destinations, classes }, picked, how);
  };
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <form className="rd-sheet fw-sheet" role="dialog" aria-modal="true" aria-labelledby="fw-sheet-title" ref={ref} tabIndex={-1}
        onKeyDown={(e) => e.key === "Escape" && onClose()} onSubmit={submit}>
        <header className="rd-sheet-head">
          <div className="rd-sheet-heading">
            <span className="rd-sheet-eyebrow">{W.GROUPS.title}</span>
            <h2 id="fw-sheet-title">{isNew ? W.GROUPS.create : `Change ${profile.name}`}</h2>
          </div>
          <button type="button" className="rd-sheet-close" onClick={onClose} aria-label="Close"><FiX size={18} /></button>
        </header>
        <div className="rd-sheet-body">
          <section className="fw-field">
            <h3><span className="fw-step">1</span> Name it</h3>
            <input className="fw-input" value={name} maxLength={DW.MAX_PROFILE_NAME} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Tills" aria-label="Group name" />
            {isNew && (
              <div className="fw-templates">
                {W.GROUPS.templates.map((t) => (
                  <button key={t} type="button" className={`fw-pill${name === t ? " is-on" : ""}`} onClick={() => setName(t)}>{t}</button>
                ))}
              </div>
            )}
            <p className="fw-muted">{W.SAME_NAME}</p>
          </section>

          <section className="fw-field">
            <h3><span className="fw-step">2</span> Pick its devices <em>{W.plural(picked.length, "picked", "picked")}</em></h3>
            <DevicePicker rows={rows} picked={picked} setPicked={setPicked} self={self} nameOf={nameOf} />
          </section>

          <section className="fw-field">
            <h3><span className="fw-step">3</span> Where they can go</h3>
            <div className="fw-choices" role="radiogroup" aria-label="Where they can go">
              {["learn", "only", "anywhere"].map((c) => (
                <label key={c} className={`fw-choice${how === c ? " is-on" : ""}`}>
                  <input type="radio" name="how" checked={how === c} onChange={() => setHow(c)} />
                  <span className="fw-choice-title">{W.HOW[c].title}{W.HOW[c].tag && <em>{W.HOW[c].tag}</em>}</span>
                  <span className="fw-choice-text">{W.HOW[c].text}</span>
                </label>
              ))}
            </div>
            {how === "only" && (
              <div className="fw-only">
                <AddressChips values={places} onChange={setPlaces} placeholder={W.PLACE_ADD} label="Place to add" disabled={busy} />
                {watched?.suggested?.length > 0 && (
                  <button type="button" className="fw-btn is-ai" onClick={() => setPlaces(watched.suggested)}>
                    <AiIcon size={15} aria-hidden="true" /> {W.USE_LEARNED} ({watched.suggested.length})
                  </button>
                )}
                <Learned watched={watched} />
                <p className="fw-inline-note is-warn">{newDevices ? W.NEW_DEVICES_HELD : W.HELD_LOSES}</p>
              </div>
            )}
          </section>

          <section className="fw-field">
            <h3><span className="fw-step">4</span> Block kinds of traffic <em className="is-quiet">{W.OPTIONAL}</em></h3>
            {!controls.tunnel && <p className="fw-muted">{W.BLOCK_NEEDS_TUNNEL}</p>}
            <KindPills classList={classList} blocked={blocked} setBlocked={setBlocked} taken={Object.keys(lineRows)} />
          </section>

          <details className="fw-how">
            <summary>{W.HOW_IT_WORKS}</summary>
            <p>{W.LOOKUPS_PASS}</p>
            <p>{W.BLOCK_CAVEAT}</p>
            <p>{W.GROUPS.shared}</p>
          </details>
        </div>
        <footer className="rd-sheet-foot">
          {!isNew && (
            <button type="button" className="fw-btn is-danger-quiet rd-stop" onClick={onDelete} disabled={busy}>
              <FiTrash2 size={14} aria-hidden="true" /> Delete group
            </button>
          )}
          <button type="button" className="fw-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="fw-btn is-primary"
            disabled={busy || !name.trim() || (how === "only" && places.length === 0)}>
            {isNew ? "Create group" : "Save"}
          </button>
        </footer>
      </form>
    </>
  );
}

/* The group as a drawing: its devices, a line, and where they may go. */
function Lane({ tone, devices, label, children, stub }) {
  return (
    <div className={`fw-glane is-${tone}`}>
      <ul className="fw-glane-from">
        {devices.slice(0, 6).map((d) => {
          const Kind = kindIcon(d);
          return <li key={d}><Kind size={14} strokeWidth={1.8} aria-hidden="true" /> {d}</li>;
        })}
        {devices.length > 6 && <li className="is-more">{W.moreDevices(devices.length - 6)}</li>}
        {devices.length === 0 && <li className="is-more">{W.NO_DEVICES_YET}</li>}
      </ul>
      <div className="fw-glane-line">
        <span className="fw-glane-label">{label}</span>
        {stub && <span className="fw-glane-stub"><TbBan size={13} aria-hidden="true" /> {stub}</span>}
      </div>
      <div className="fw-glane-to">{children}</div>
    </div>
  );
}

/* A place: who runs it, as people say it, with its address under the name. */
const Place = ({ net, owner, tag, tone }) => (
  <li className={tone ? `is-${tone}` : undefined}>
    <strong title={owner || undefined}>{W.orgName(owner) || W.shown(net)}</strong>
    {owner && <code>{W.shown(net)}</code>}
    {tag && <em>{tone === "odd" && <TbAlertTriangle size={12} aria-hidden="true" />} {tag}</em>}
  </li>
);

/* What learning offers: the places to lock to, those the group uses at the
   customer's other sites too, and what is left out and who went there. */
function Suggestion({ group, watched, offer, busy, onLock }) {
  const nets = [...offer.take.map((r) => r.net), ...offer.elsewhere.map((r) => r.net)];
  return (
    <div className="fw-suggest">
      <div className="fw-suggest-head">
        <AiIcon size={18} aria-hidden="true" />
        <p>{W.suggestionWords(watched, nets.length)}</p>
      </div>
      <table className="fw-suggest-table">
        <thead><tr><th>{W.SUGGEST_COLS.who}</th><th>{W.SUGGEST_COLS.port}</th><th>{W.SUGGEST_COLS.devices}</th><th>{W.SUGGEST_COLS.last}</th></tr></thead>
        <tbody>
          {offer.take.map((r) => (
            <tr key={r.net}>
              <td><ul className="fw-places is-inline"><Place net={r.net} owner={r.owner} /></ul></td>
              <td data-label={W.SUGGEST_COLS.port}>{r.ports}</td>
              <td data-label={W.SUGGEST_COLS.devices}>{r.devices}</td>
              <td data-label={W.SUGGEST_COLS.last}>{r.last}</td>
            </tr>
          ))}
          {offer.elsewhere.map((r) => (
            <tr key={r.net} className="is-elsewhere">
              <td><ul className="fw-places is-inline"><Place net={r.net} owner={r.owner} tag={W.elsewhereWords(r.sites)} tone="elsewhere" /></ul></td>
              <td data-label={W.SUGGEST_COLS.port}>{r.ports}</td>
              <td data-label={W.SUGGEST_COLS.devices}>–</td>
              <td data-label={W.SUGGEST_COLS.last}>–</td>
            </tr>
          ))}
        </tbody>
      </table>
      {offer.left.length > 0 && (
        <div className="fw-left">
          <strong>{W.LEFT_OUT}</strong>
          {offer.left.map((r) => (
            <p key={r.net}><TbAlertTriangle size={15} aria-hidden="true" /> {W.leftOutWords(r, group)}</p>
          ))}
        </div>
      )}
      <div className="fw-suggest-go">
        <button type="button" className="fw-btn is-primary" disabled={busy || !nets.length} onClick={() => onLock(nets)}>
          {W.lockTo(group, nets.length)}
        </button>
      </div>
    </div>
  );
}

function GroupCard({ profile, controls, members, held, watched, owners, offer, labelFor, learnable, busy, onEdit, onWatch, onLock }) {
  const state = W.groupState(profile, watched);
  const Icon = STATE_ICONS[state.tone];
  const places = profile.destinations || [];
  const blocked = Object.keys(DW.blockedClasses(profile.classes)).map(labelFor);
  // Learning on a locked group would only see what the lock lets through.
  const canLearn = learnable && (watched || !places.length);
  const stopped = Number(held?.stopped || 0);
  const seen = W.byOwner((watched?.places || []).filter((p) => p.answered));
  return (
    <article className={`fw-group is-${state.tone}`}>
      <header>
        <span className="fw-group-icon" aria-hidden="true"><Icon size={20} strokeWidth={1.8} /></span>
        <div className="fw-group-name">
          <h4>{profile.name} <span className={`fw-chip is-${state.tone}`}>{state.label}</span></h4>
          <span>{W.membersWords(profile, controls)}</span>
        </div>
        <div className="fw-group-actions">
          {canLearn && (
            <button type="button" className="fw-btn is-quiet" disabled={busy} onClick={() => onWatch(!watched)}>
              {watched ? <><FiEyeOff size={14} aria-hidden="true" /> Stop learning</> : <><AiIcon size={15} aria-hidden="true" /> Start learning</>}
            </button>
          )}
          <button type="button" className="fw-btn" onClick={onEdit}><FiEdit2 size={14} aria-hidden="true" /> Edit</button>
        </div>
      </header>
      <Lane tone={state.tone} devices={members} label={W.laneLabel(state.tone, watched)}
        stub={places.length && stopped ? W.stoppedStub(stopped) : ""}>
        {places.length > 0 && (
          <ul className="fw-places">
            {places.map((p) => <Place key={p} net={p} owner={owners[p] || W.ownerIn(p, watched?.places)} />)}
          </ul>
        )}
        {!places.length && watched && (
          seen.length ? (
            <ul className="fw-places">
              {seen.slice(0, 3).map((g) => <Place key={g.key} net={g.where} owner={g.owner} />)}
              {seen.length > 3 && <li className="is-more">{W.morePlaces(seen.length - 3)}</li>}
            </ul>
          ) : <p className="fw-glane-seen">{W.NOTHING_SEEN_YET}</p>
        )}
        {!places.length && !watched && <p className="fw-glane-any"><TbWorld size={16} aria-hidden="true" /> {W.ANYWHERE}</p>}
      </Lane>
      {blocked.length > 0 && (
        <p className="fw-group-blocked"><TbBan size={14} aria-hidden="true" /> {W.blockedKinds(blocked)}</p>
      )}
      {!places.length && (offer.take.length > 0 || offer.elsewhere.length > 0) && (
        <Suggestion group={profile.name} watched={watched} offer={offer} busy={busy} onLock={onLock} />
      )}
    </article>
  );
}

/* Devices whose names look alike, offered as a group. */
function Alike({ alike, onCreate }) {
  const Kind = kindIcon(alike.names[0]);
  return (
    <div className="fw-alike">
      <span className="fw-alike-icon" aria-hidden="true"><Kind size={20} strokeWidth={1.8} /></span>
      <p>{W.alikeWords(alike)}</p>
      <button type="button" className="fw-btn is-ai" onClick={() => onCreate({ name: alike.name, macs: alike.macs })}>
        <TbUsersPlus size={15} aria-hidden="true" /> {W.makeGroup(alike.name)}
      </button>
    </div>
  );
}

/* No group yet: what a group is for, in three steps, and one way to start,
   offered first for devices that look alike. */
function Empty({ alike, onCreate }) {
  return (
    <div className="fw-card fw-empty">
      <h4>{W.GROUPS.emptyTitle}</h4>
      <ol className="fw-steps is-wide">
        {W.GROUPS.steps.map((s, i) => (
          <li key={s.title}><span>{i + 1}</span><div><strong>{s.title}</strong><p>{s.text}</p></div></li>
        ))}
      </ol>
      {alike.map((a) => <Alike key={a.name} alike={a} onCreate={onCreate} />)}
      {!alike.length && (
        <button type="button" className="fw-btn is-primary is-big" onClick={() => onCreate({ name: "" })}>
          <FiPlus size={15} aria-hidden="true" /> {W.GROUPS.create}
        </button>
      )}
    </div>
  );
}

export default function FirewallGroups({ selectedNode, firewall, classList, dc, learn, fleet, rows }) {
  const watchWrite = useEdgeWrite(selectedNode, learn.refresh);
  const confirm = useConfirm();
  const [editing, setEditing] = useState(null); // { id } or { isNew, name, macs }
  const [working, setWorking] = useState(false);
  const [undo, setUndo] = useState(null); // { profile, before }
  const view = dc.view;
  const learned = learn.state === FEATURE_PRESENT && learn.payload?.available ? learn.payload : null;
  useEffect(() => {
    if (!undo) return undefined;
    const t = setTimeout(() => setUndo(null), W.UNDO_FOR_MS);
    return () => clearTimeout(t);
  }, [undo]);

  if (!view) return <section className="fw-card"><p className="fw-muted">Reading the device groups…</p></section>;
  if (!view.available) {
    return (
      <section className="fw-card">
        <h3 className="fw-card-title">{W.GROUPS.title}</h3>
        <p className="fw-muted">{view.reason}</p>
      </section>
    );
  }
  const profiles = view.profiles || [];
  const assigned = view.devices || {};
  const nameOfMac = (mac) => rows.find((d) => d.mac === mac)?.name || mac;
  const watchedOf = (id) => (learned?.groups || []).find((g) => g.group === id);
  const heldOf = (id) => (firewall?.groups || []).find((g) => g.id === id);
  const labelFor = (id) => classList.find((c) => c.key === id)?.label || id;
  const nameOf = (id) => profiles.find((p) => p.id === id)?.name || id;
  const sameName = (name) => (x) => String(x).toLowerCase() === name.toLowerCase();
  const offerFor = (p) => W.suggestion(watchedOf(p.id), {
    odd: (fleet?.odd || []).filter((o) => sameName(p.name)(o.group)),
    fleetGroup: (fleet?.groups || []).find((g) => sameName(p.name)(g.name)),
    here: selectedNode,
    nameOfMac,
  });
  const watch = (id, on) => watchWrite.send("POST", "/firewall/learn", { group: id, watch: on });
  const askWatch = async (p, on) => {
    if (!(await confirm(on ? W.watchAsk(p) : W.STOP_WATCH_ASK))) return;
    await watch(p.id, on);
  };

  /* Save the group, then its devices one by one, then whether it learns. */
  const save = async (p, picked, how) => {
    setWorking(true);
    try {
      const out = await dc.saveProfile(p);
      if (!out) return;
      const id = out.id || p.id;
      const before = Object.entries(assigned).filter(([, a]) => a.profile === id).map(([mac]) => mac);
      for (const mac of picked.filter((m) => !before.includes(m))) if (!(await dc.assign(mac, id))) return;
      for (const mac of before.filter((m) => !picked.includes(m))) if (!(await dc.assign(mac, ""))) return;
      const watching = !!watchedOf(id);
      if (how === "learn" && !watching && learned) await watch(id, true);
      if (how === "anywhere" && watching && (await confirm(W.STOP_WATCH_ASK))) await watch(id, false);
      setEditing(null);
    } finally {
      setWorking(false);
    }
  };
  const lock = async (p, places) => {
    if (view.new_devices === p.id && !(await confirm(W.NEW_DEVICES_ASK))) return;
    if (await dc.saveProfile({ ...p, destinations: places })) setUndo({ profile: p, before: p.destinations || [] });
  };
  const unlock = async () => {
    const { profile, before } = undo;
    setUndo(null);
    await dc.saveProfile({ ...profile, destinations: before });
  };
  const remove = async (p) => {
    if (!(await confirm(W.deleteAsk(p)))) return;
    if (watchedOf(p.id)) await watch(p.id, false);
    if (await dc.deleteProfile(p.id)) setEditing(null);
  };

  const busy = dc.busy || watchWrite.busy || working;
  const error = dc.error || watchWrite.error;
  const alike = W.lookAlikes(rows, profiles);
  const create = (start) => setEditing({ isNew: true, ...start });
  const open = editing && (editing.isNew ? { name: editing.name } : profiles.find((p) => p.id === editing.id));
  return (
    <div className="fw-groups-page">
      <header className="fw-page-head">
        <div>
          <h3 className="fw-card-title">{W.GROUPS.title}</h3>
          <p>{W.GROUPS.means}</p>
        </div>
        {profiles.length > 0 && (
          <button type="button" className="fw-btn is-primary" onClick={() => create({ name: "" })}>
            <FiPlus size={15} aria-hidden="true" /> {W.GROUPS.create}
          </button>
        )}
      </header>
      {error && <div className="fw-notice is-bad" role="alert">{error}</div>}
      {undo && (
        <div className="fw-notice is-ok" role="status">
          <TbLock size={16} aria-hidden="true" /> <span>{W.lockedWords(undo.profile.name)}</span>
          <button type="button" className="fw-btn is-quiet" onClick={unlock} disabled={busy}>{W.UNDO}</button>
        </div>
      )}
      {profiles.length === 0 && <Empty alike={alike} onCreate={create} />}
      {profiles.length > 0 && alike.map((a) => <Alike key={a.name} alike={a} onCreate={create} />)}
      {profiles.map((p) => (
        <GroupCard key={p.id} profile={p} controls={view} held={heldOf(p.id)} watched={watchedOf(p.id)}
          owners={learned?.owners || {}} offer={offerFor(p)}
          members={rows.filter((d) => d.profile === p.id).map((d) => d.name)}
          labelFor={labelFor} learnable={!!learned} busy={busy}
          onEdit={() => setEditing({ id: p.id })} onWatch={(on) => askWatch(p, on)} onLock={(places) => lock(p, places)} />
      ))}
      {open && (
        <GroupSheet key={open.id || "new"} profile={open} isNew={!!editing.isNew} start={editing} controls={view} rows={rows}
          classList={classList} watched={open.id ? watchedOf(open.id) : null} busy={busy} nameOf={nameOf}
          onSave={save} onClose={() => setEditing(null)} onDelete={() => remove(open)} />
      )}
    </div>
  );
}
